import React, { Fragment, useContext, useEffect, useState, useRef } from 'react';
import { IFilterColumn } from './index';
import FilterControlSelect from 'components/report-content/components/manage-filters/parts/filter/FilterControlSelect';
import FilterControlRange from 'components/report-content/components/manage-filters/parts/filter/FilterControlRange';
import FilterControlChartingInterval from 'components/report-content/components/manage-filters/parts/filter/FilterControlChartingInterval';
import getFilterConditionRules from 'components/report-content/components/manage-filters/helpers/getFilterConditionRules';
import buildFilterColumns from 'components/report-content/components/manage-filters/helpers/buildFilterColumns';
import { useLocation } from 'react-router-dom';
import { AssocArray } from 'tools/types';
import ReactSelect from 'components/common/react-select/ReactSelect';
import { Box, Button, Stack, Tooltip } from '@mui/material';
import { StaticAddon } from 'components/common/static-addon/StaticAddon';
import { ReportFilterSegment } from 'components/report-content/hooks/useReportContentSettings';
import IconMi from 'components/common/icon/IconMi';
import useBundleTranslation from 'i18n';
import { useEmbeddingContextVisible } from 'hooks/useEmbeddingContext';
import { externalReportAPI } from 'api/external-report';
import { FormComponentValue } from 'components/common/form/layout/control';
import ReportContentSkeletonPlaceholder from 'components/report-content/utils/ReportContentSkeletonPlaceholder';
import {
    ComponentSettingsManageFilters,
    ComponentUpdateProps,
    ManageFiltersInstance,
    IBlocksContext,
    BlocksContext,
} from '@report-content';
import { useInitialData } from './helpers/useInitialData';
import ReportViewerReferenceManagerIcon from 'app/report/ReportViewerReferenceManagerIcon';
import PanelWrapper from 'components/report-content/layout/PanelWrapper';

// On 'viewer' mode send request to save selected values
export function saveFiltersValue(elementId: number, segmentValueId: number, settings: ComponentSettingsManageFilters) {
    const getCurrentFilters = (onlyChangedFilters = true) => {
        const savedFilters: any = settings.fields
            .filter((f) => {
                let canBeCached = true;
                return (
                    f.filter_column === 'Y' &&
                    (f.filterType === 'multiselect' || f.filterType === 'dropdown') &&
                    (!onlyChangedFilters || canBeCached)
                );
            })
            .map((f) => ({
                name: f.column_name,
                title: f.filter_label,
                filterType: f.filterType,
                referenceName: f.reference_name_escaped,
                values: f.savedValues,
                canBeCached: true,
            }));
        if (savedFilters.length > 0) {
            return savedFilters;
        }
        return null;
    };
    const filterData = getCurrentFilters();
    if (filterData) {
        externalReportAPI.bookmark.saveFilters(elementId, segmentValueId, filterData);
    }
}

export default function ManageFilters({
    contentSettings,
    component,
    updateBlockSettings,
    actions,
    editPanel,
    blockId,
}: ComponentUpdateProps<ComponentSettingsManageFilters>) {
    const { t } = useBundleTranslation(['components/report-content']);
    const location = useLocation();
    const [filterColumns, setFilterColumns] = useState<Array<IFilterColumn>>([]);
    const [lastFilterSettings, setLastFilterSettings] = useState<any>(null);
    const { initialData, removeInitialData } = useInitialData(contentSettings, component);
    const getFilterSettings = (filters: Array<IFilterColumn>) => {
        return filters
            .sort((a, b) => (a.reference_name > b.reference_name ? 1 : -1))
            .map((filter) => ({
                name: filter.reference_name,
                type: filter.filterType,
                sort_order: filter.sortDirection,
                use_external_filter_value: filter.use_external_filter_value,
                external_delimiter: filter.external_delimiter,
                external_dataset_column_title_id: filter.external_dataset_column_title_id,
                external_dataset_column_value_id: filter.external_dataset_column_value_id,
            }));
    };

    const [urlParamsParsed, setUrlParamsParsed] = useState(false);
    const filtersSetViaUrl = useRef<string[]>([]);
    // Pass filter values from URL GET params into filters
    useEffect(() => {
        let requestParams: AssocArray<Array<string>> = {};
        if (location.search.length) {
            const urlParam = new URLSearchParams(location.search);
            for (let key of urlParam.keys()) {
                const values = urlParam.getAll(key);
                if (key.endsWith('[]')) {
                    key = key.slice(0, -2);
                }
                requestParams[key] = values;
            }
        }
        if (component.settings.fields) {
            component.settings.fields
                .filter((field) => field.filter_column == 'Y')
                .map((field) => {
                    for (const [key, value] of Object.entries(requestParams)) {
                        if (
                            key.toLowerCase() === field.reference_name.toLowerCase() ||
                            (field.column_name?.length &&
                                key.replace(/\s/g, '_').toLowerCase() ===
                                    field.column_name.replace(/\s/g, '_').toLowerCase()) ||
                            (field.filter_label?.length &&
                                key.replace(/\s/g, '_').toLowerCase() ===
                                    field.filter_label.replace(/\s/g, '_').toLowerCase())
                        ) {
                            field.savedValues = field.value_type == 'numeric' ? value.map((v) => Number(v)) : value;
                            filtersSetViaUrl.current.push(field.reference_name);
                            break;
                        }
                    }
                });
        }
        setUrlParamsParsed(true);
    }, []);

    const blocksContext = useContext<IBlocksContext | null>(BlocksContext);

    const [filtersReset, setFiltersReset] = useState(false);
    useEffect(() => {
        const newFilterColumns = buildFilterColumns(component.settings, initialData);

        const filterSettings = getFilterSettings(newFilterColumns);
        if (JSON.stringify(filterSettings) != JSON.stringify(lastFilterSettings)) {
            setLastFilterSettings(filterSettings);
            if (lastFilterSettings != null) {
                removeInitialData();
                return;
            }
        }
        let shouldResetData = false;
        const changedKeys = [
            'filterType',
            'use_external_filter_value',
            'external_delimiter',
            'external_dataset_column_title_id',
            'external_dataset_column_value_id',
        ] as const;

        if (filterColumns.length != newFilterColumns.length) {
            shouldResetData = true;
        } else {
            filterColumns.forEach((p) => {
                const newFilter = newFilterColumns.find((n) => n.reference_name == p.reference_name);
                if (!newFilter || changedKeys.some((k) => newFilter[k] != p[k])) {
                    shouldResetData = true;
                }
            });
        }

        if (!shouldResetData && blocksContext) {
            const rules = getFilterConditionRules(component, newFilterColumns);
            blocksContext.setFilters(rules);
        }

        let valuesWasChanged = false;
        newFilterColumns.forEach((filter) => {
            if (filter.data == '' || (filter.filterType != 'dropdown' && filter.filterType != 'multiselect')) {
                return;
            }

            const allowedList = Array.isArray(filter.data)
                ? filter.data.map((row: any) => {
                      return row[filter.uid];
                  })
                : [];

            let newValues = filter.savedValues.filter((v) => allowedList.includes(v));
            if (urlParamsParsed && !filtersReset) {
                // Reset filter value if it was NOT set via URL, but at least one of them was.
                if (filtersSetViaUrl.current.length) {
                    if (!filtersSetViaUrl.current.includes(filter.reference_name)) {
                        if (filter.filterType == 'dropdown') {
                            newValues = filter.include_all_value == 'Y' ? [] : (allowedList?.[0] ?? []);
                        } else {
                            newValues = [];
                        }
                    }
                }
                setFiltersReset(true);
            }

            if (JSON.stringify(newValues) != JSON.stringify(filter.savedValues)) {
                filter.savedValues = newValues;
                valuesWasChanged = true;
            }
        });

        if (valuesWasChanged) {
            const fields = component.settings.fields.slice();
            fields.forEach((f) => {
                const filter = newFilterColumns.find((filter) => filter.uid == f.reference_name_escaped);
                if (filter) {
                    f.savedValues = filter.savedValues;
                }
            });
            updateBlockSettings(
                { ...component.settings, fields: fields },
                { shouldDirty: blocksContext?.manageFiltersReady },
            );
        }
        setFilterColumns(newFilterColumns);
    }, [component.settings, initialData, urlParamsParsed]);

    useEffect(() => {
        if (!initialData || !blocksContext) {
            return;
        }
        setTimeout(() => blocksContext.setManageFiltersReady(true), 100);
    }, [initialData]);
    const isVisibleSegment = useEmbeddingContextVisible('segment');
    const isVisibleFilters = useEmbeddingContextVisible('eFilters');

    const handleFilterChange = (filter: IFilterColumn, value: any) => {
        if (!component.settings.fields) {
            return;
        }
        const index = component.settings.fields.findIndex((f) => f.reference_name_escaped == filter.uid);

        if (index == -1) {
            return;
        }

        const fields = component.settings.fields.slice();
        fields[index].savedValues = Array.isArray(value) ? value : [value];
        if (contentSettings.context == 'view') {
            saveFiltersValue(contentSettings.elementId, contentSettings.segmentValueId, component.settings);
        }
        updateBlockSettings({ ...component.settings, fields: fields });
    };
    if (!initialData) {
        return (
            <Stack spacing={3} direction={'column'}>
                <ReportContentSkeletonPlaceholder height={54} />
                <ReportContentSkeletonPlaceholder height={600} />
            </Stack>
        );
    }

    // Hide whole filters block if no filters available
    const hasInst =
        contentSettings.hasInstances && contentSettings.instances.length && contentSettings.measurement_time;
    if (
        contentSettings.context == 'view' &&
        !(
            hasInst ||
            contentSettings.related.length > 1 ||
            (contentSettings.segmentValues.segments.length > 0 && isVisibleSegment) ||
            filterColumns.length
        )
    ) {
        return null;
    }

    return (
        <Box>
            <Box
                className={'filter-block-scroll-overlap'}
                sx={{
                    position: 'absolute',
                    bottom: '100%',
                    left: 0,
                    right: 0,
                    backgroundColor: 'background.default',
                    height: '32px',
                }}
            />
            {isVisibleFilters && (
                <PanelWrapper>
                    <Box>
                        {hasInst && (
                            <Box sx={{ width: '264px', display: 'inline-flex', m: 0.75 }}>
                                <StaticAddon sx={{ maxWidth: '110px' }}>
                                    <Tooltip placement="bottom-start" enterDelay={1000} title={t('snapshot_date')}>
                                        <span>{t('snapshot_date')}</span>
                                    </Tooltip>
                                </StaticAddon>
                                <Box sx={{ flexGrow: 1 }}>
                                    <InstanceSelect
                                        instances={contentSettings.instances}
                                        selected={contentSettings.measurement_time ?? ''}
                                        onChange={(value) => actions.updateMeasurementTime(value)}
                                    />
                                </Box>
                            </Box>
                        )}
                        {contentSettings.context == 'view' && contentSettings.related.length > 1 && (
                            <Box sx={{ width: '264px', display: 'inline-flex', m: 0.75 }}>
                                <StaticAddon sx={{ maxWidth: '110px' }}>
                                    <Tooltip placement="bottom-start" enterDelay={1000} title={t('interval')}>
                                        <span>{t('interval')}</span>
                                    </Tooltip>
                                </StaticAddon>
                                <Box sx={{ flexGrow: 1 }}>
                                    <RelatedSelect
                                        data={contentSettings.related}
                                        selected={
                                            (
                                                contentSettings.related.find((r) => r.props.selected) ??
                                                contentSettings.related[0]
                                            ).value
                                        }
                                        onChange={(value: any) => {
                                            window.location.href = value;
                                        }}
                                    />
                                </Box>
                            </Box>
                        )}
                        {contentSettings.segmentValues.segments.length > 0 && isVisibleSegment && (
                            <Box sx={{ width: '264px', display: 'inline-flex', m: 0.75 }}>
                                <StaticAddon sx={{ maxWidth: '110px' }}>
                                    <Tooltip
                                        placement="bottom-start"
                                        enterDelay={1000}
                                        title={contentSettings.segmentValues.segments[0].name}
                                    >
                                        <span>{contentSettings.segmentValues.segments[0].name} </span>
                                    </Tooltip>
                                </StaticAddon>
                                <SegmentValueSelect
                                    segment={contentSettings.segmentValues.segments[0]}
                                    selected={String(contentSettings.segmentValueId)}
                                    onChange={(value) => actions.updateSegmentValue(value)}
                                />
                            </Box>
                        )}
                        {filterColumns
                            .sort((a, b) => a.column_sequence - b.column_sequence)
                            .map((fc) => {
                                let isDouble = false;
                                let component: any = 'Component not FOUND: ' + fc.filterType;
                                switch (fc.filterType) {
                                    case 'multiselect':
                                    case 'dropdown':
                                        component = (
                                            <FilterControlSelect
                                                filter={fc}
                                                onChange={(value) => handleFilterChange(fc, value)}
                                            />
                                        );
                                        break;
                                    case 'range':
                                        isDouble = true;
                                        component = (
                                            <FilterControlRange
                                                filter={fc}
                                                onChange={(value) => handleFilterChange(fc, value)}
                                            />
                                        );
                                        break;
                                    case 'charting_interval':
                                        component = (
                                            <FilterControlChartingInterval
                                                filter={fc}
                                                onChange={(value) => handleFilterChange(fc, value)}
                                            />
                                        );
                                }

                                if (isDouble) {
                                    return <Fragment key={fc.column_name}>{component}</Fragment>;
                                }

                                return (
                                    <Box
                                        sx={{
                                            width: '264px',
                                            display: 'inline-flex',
                                            m: 0.75,
                                        }}
                                        key={fc.column_name}
                                    >
                                        <StaticAddon sx={{ maxWidth: '110px' }}>
                                            <Tooltip placement="bottom-start" enterDelay={1000} title={fc.column_name}>
                                                <span>{fc.column_name}</span>
                                            </Tooltip>
                                        </StaticAddon>
                                        {component}
                                    </Box>
                                );
                            })}
                        {contentSettings.context == 'view' && urlParamsParsed && (
                            <Box sx={{ display: 'inline-flex', verticalAlign: 'top', mt: 0.75 }}>
                                <ReportViewerReferenceManagerIcon
                                    actions={actions}
                                    initialData={initialData}
                                    contentSettings={contentSettings}
                                    component={component}
                                    updateBlockSettings={updateBlockSettings}
                                    setDefaultBookmark={filtersSetViaUrl.current.length == 0}
                                />
                            </Box>
                        )}
                    </Box>
                    {contentSettings.context == 'edit' && (
                        <Box sx={{ m: 0.75 }}>
                            <Button
                                sx={{ whiteSpace: 'nowrap' }}
                                startIcon={<IconMi icon="gear" />}
                                onClick={() => actions.openBlockEditor(String(blockId), editPanel)}
                                data-test={'edit-filters-button'}
                            >
                                {t('manage_filters.edit_filters')}
                            </Button>
                        </Box>
                    )}
                </PanelWrapper>
            )}
        </Box>
    );
}

function InstanceSelect({
    instances,
    selected,
    onChange,
}: {
    instances: Array<ManageFiltersInstance>;
    selected: string;
    onChange: (values: string) => void;
}) {
    const data = instances.map((inst) => ({ label: inst.measurement_time, value: inst.measurement_time_raw }));

    return (
        <Box sx={{ flexGrow: 1 }}>
            <ReactSelect
                dataTest={'instance-value'}
                data={data}
                update={(value) => {
                    onChange(value);
                }}
                value={selected}
            />
        </Box>
    );
}

function SegmentValueSelect({
    segment,
    selected,
    onChange,
}: {
    segment: ReportFilterSegment;
    selected: string;
    onChange: (values: string) => void;
}) {
    const data = segment.values.map((v) => ({ label: v.segment, value: String(v.segment_value_id) }));

    return (
        <Box sx={{ flexGrow: 1 }}>
            <ReactSelect
                dataTest={'segment-value'}
                data={data}
                update={(value) => {
                    onChange(value);
                }}
                value={selected}
            />
        </Box>
    );
}

function RelatedSelect({
    data,
    selected,
    onChange,
}: {
    data: Array<FormComponentValue>;
    selected: string;
    onChange: (values: string) => void;
}) {
    return (
        <Box sx={{ flexGrow: 1 }}>
            <ReactSelect
                dataTest={'related-value'}
                data={data}
                update={(value) => {
                    onChange(value);
                }}
                value={selected}
            />
        </Box>
    );
}
